import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = 'https://www.lankastudio.cn/api';
const MAX_IMAGES = 9;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

const categories = [
  { id: 'all', name: '全部', icon: '📋', color: '#12B7F5' },
  { id: '改装分享', name: '改装分享', icon: '🎨', color: '#10B981' },
  { id: '新手提问', name: '新手提问', icon: '❓', color: '#F59E0B' },
  { id: '教程攻略', name: '教程攻略', icon: '📚', color: '#8B5CF6' },
  { id: '开箱晒物', name: '开箱晒物', icon: '📦', color: '#EC4899' },
  { id: '闲聊灌水', name: '闲聊灌水', icon: '💬', color: '#6B7280' },
];

function Forum() {
  const { user, isAuthenticated } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newPost, setNewPost] = useState({ title: '', content: '', category: '闲聊灌水' });
  
  // 图片上传状态
  const [images, setImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // 数据状态
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // 编辑帖子状态
  const [editingPost, setEditingPost] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editImages, setEditImages] = useState([]);
  
  // 删除确认状态
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // 加载帖子列表
  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, searchTerm]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/forum/posts?`;
      
      if (selectedCategory !== 'all') {
        url += `category=${encodeURIComponent(selectedCategory)}&`;
      }
      if (searchTerm) {
        url += `search=${encodeURIComponent(searchTerm)}&`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('获取帖子失败');
      
      const data = await response.json();
      setPosts(data.posts || []);
      setError(null);
    } catch (err) {
      console.error('获取帖子失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 格式化时间
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return `${minutes} 分钟前`;
      }
      return `${hours} 小时前`;
    } else if (days < 7) {
      return `${days} 天前`;
    } else {
      return date.toLocaleDateString('zh-CN');
    }
  };

  // 获取分类颜色
  const getCategoryColor = (categoryName) => {
    const cat = categories.find(c => c.name === categoryName || c.id === categoryName);
    return cat ? cat.color : '#6B7280';
  };

  // 处理图片选择
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    processImages(files);
  };

  // 处理拖拽
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      processImages(files);
    }
  };

  // 处理图片文件
  const processImages = async (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('请选择图片文件');
      return;
    }

    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      alert(`最多只能上传 ${MAX_IMAGES} 张图片`);
      return;
    }

    const filesToProcess = imageFiles.slice(0, remainingSlots);
    setUploadingImages(true);

    try {
      const newImages = await Promise.all(
        filesToProcess.map(async (file) => {
          if (file.size > MAX_IMAGE_SIZE) {
            throw new Error(`图片 ${file.name} 超过 5MB 限制`);
          }
          
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve({
                id: Date.now() + Math.random(),
                url: e.target.result,
                name: file.name,
                size: file.size
              });
            };
            reader.onerror = () => reject(new Error('读取图片失败'));
            reader.readAsDataURL(file);
          });
        })
      );

      setImages(prev => [...prev, ...newImages]);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingImages(false);
    }
  };

  // 删除图片
  const removeImage = (imageId) => {
    setImages(prev => prev.filter(img => img.id !== imageId));
  };

  // 重置表单
  const resetForm = () => {
    setNewPost({ title: '', content: '', category: '闲聊灌水' });
    setImages([]);
  };

  // 处理发帖
  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPost.title.trim() || !newPost.content.trim()) return;
    if (!user?.id) {
      alert('请先登录');
      return;
    }

    setSubmitting(true);
    try {
      // 准备图片数据
      const imageUrls = images.map(img => img.url);

      const response = await fetch(`${API_BASE_URL}/forum/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          author_id: user.id,
          author_nickname: user.nickname || '匿名用户',
          author_avatar: user.avatar || user.figureurl_qq_2 || user.figureurl_2,
          title: newPost.title,
          content: newPost.content,
          category: newPost.category,
          images: imageUrls
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '发布失败');
      }

      const newPostData = await response.json();
      
      // 将新帖子添加到列表顶部
      setPosts(prev => [newPostData, ...prev]);
      
      // 重置表单
      resetForm();
      setShowNewPostModal(false);
      
      alert('发布成功！');
    } catch (err) {
      console.error('发布帖子失败:', err);
      alert('发布失败：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // 打开编辑弹窗
  const handleEditPost = (post) => {
    setEditingPost({
      id: post.id,
      title: post.title,
      content: post.content,
      category: post.category
    });
    // 设置编辑时的图片
    setEditImages(post.images || []);
    setShowEditModal(true);
  };

  // 提交编辑
  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!editingPost || !editingPost.title.trim() || !editingPost.content.trim()) return;

    setSubmitting(true);
    try {
      // 处理图片 - 已有的URL直接使用，新的base64需要上传
      const imageUrls = editImages.map(img => 
        typeof img === 'string' ? img : (img.url || img)
      );

      const response = await fetch(`${API_BASE_URL}/forum/posts/${editingPost.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingPost.title,
          content: editingPost.content,
          category: editingPost.category,
          images: imageUrls
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '更新失败');
      }

      // 更新本地帖子列表
      setPosts(prev => prev.map(post => {
        if (post.id === editingPost.id) {
          return {
            ...post,
            title: editingPost.title,
            content: editingPost.content,
            category: editingPost.category,
            images: imageUrls
          };
        }
        return post;
      }));

      // 关闭弹窗
      setShowEditModal(false);
      setEditingPost(null);
      setEditImages([]);
      
      alert('更新成功！');
    } catch (err) {
      console.error('更新帖子失败:', err);
      alert('更新失败：' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // 删除帖子
  const handleDeletePost = async (postId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '删除失败');
      }

      // 从本地列表移除
      setPosts(prev => prev.filter(post => post.id !== postId));
      setDeleteConfirm(null);
      
      alert('删除成功！');
    } catch (err) {
      console.error('删除帖子失败:', err);
      alert('删除失败：' + err.message);
    }
  };

  // 编辑时添加图片
  const handleEditImageSelect = (e) => {
    const files = Array.from(e.target.files);
    processEditImages(files);
  };

  const processEditImages = async (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    const remainingSlots = MAX_IMAGES - editImages.length;
    
    if (remainingSlots <= 0) {
      alert(`最多只能上传 ${MAX_IMAGES} 张图片`);
      return;
    }

    const filesToProcess = imageFiles.slice(0, remainingSlots);

    try {
      const newImages = await Promise.all(
        filesToProcess.map(async (file) => {
          if (file.size > MAX_IMAGE_SIZE) {
            throw new Error(`图片 ${file.name} 超过 5MB 限制`);
          }
          
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = () => reject(new Error('读取图片失败'));
            reader.readAsDataURL(file);
          });
        })
      );

      setEditImages(prev => [...prev, ...newImages]);
    } catch (err) {
      alert(err.message);
    }
  };

  // 编辑时删除图片
  const removeEditImage = (index) => {
    setEditImages(prev => prev.filter((_, i) => i !== index));
  };

  // 处理点赞
  const handleLike = async (postId) => {
    if (!user?.id) {
      alert('请先登录');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/forum/posts/${postId}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id })
      });

      if (!response.ok) throw new Error('点赞失败');
      
      const result = await response.json();
      
      // 更新本地状态
      setPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            likes: result.liked ? post.likes + 1 : post.likes - 1
          };
        }
        return post;
      }));
    } catch (err) {
      console.error('点赞失败:', err);
    }
  };

  return (
    <div style={styles.container}>
      {/* 背景装饰 */}
      <div style={styles.bgDecoration}></div>
      
      {/* 主内容区 */}
      <div style={styles.content}>
        {/* 页面标题 */}
        <div style={styles.header}>
          <div style={styles.titleSection}>
            <h1 style={styles.pageTitle}>
              <span style={styles.titleIcon}>💬</span>
              社区论坛
            </h1>
            <p style={styles.pageSubtitle}>与模玩爱好者交流心得、分享作品</p>
          </div>
          
          {isAuthenticated && (
            <button 
              style={styles.newPostBtn}
              onClick={() => setShowNewPostModal(true)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(18, 183, 245, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(18, 183, 245, 0.3)';
              }}
            >
              <span style={styles.plusIcon}>+</span>
              发布帖子
            </button>
          )}
        </div>

        {/* 搜索栏 */}
        <div style={styles.searchBar}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="搜索帖子..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* 分类标签 */}
        <div style={styles.categoryBar}>
          {categories.map(cat => (
            <button
              key={cat.id}
              style={{
                ...styles.categoryTag,
                backgroundColor: selectedCategory === cat.id ? cat.color : 'transparent',
                borderColor: selectedCategory === cat.id ? cat.color : '#3d4450',
                color: selectedCategory === cat.id ? '#fff' : '#9ca3af',
              }}
              onClick={() => setSelectedCategory(cat.id)}
            >
              <span>{cat.icon}</span>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>

        {/* 帖子列表 */}
        <div style={styles.postList}>
          {loading ? (
            <div style={styles.loadingState}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>加载中...</p>
            </div>
          ) : error ? (
            <div style={styles.errorState}>
              <span style={styles.errorIcon}>❌</span>
              <p style={styles.errorText}>{error}</p>
              <button style={styles.retryBtn} onClick={fetchPosts}>重试</button>
            </div>
          ) : posts.length === 0 ? (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>📭</span>
              <p style={styles.emptyText}>暂无相关帖子</p>
              {isAuthenticated && (
                <button 
                  style={styles.createFirstBtn}
                  onClick={() => setShowNewPostModal(true)}
                >
                  发布第一个帖子
                </button>
              )}
            </div>
          ) : (
            posts.map(post => (
              <PostCard 
                key={post.id} 
                post={post} 
                formatTime={formatTime}
                getCategoryColor={getCategoryColor}
                onLike={handleLike}
                onEdit={handleEditPost}
                onDelete={(id) => setDeleteConfirm(id)}
                isAuthenticated={isAuthenticated}
                currentUserId={user?.id}
              />
            ))
          )}
        </div>

        {/* 未登录提示 */}
        {!isAuthenticated && (
          <div style={styles.loginPrompt}>
            <p>登录后即可发布帖子、参与讨论</p>
          </div>
        )}
      </div>

      {/* 发帖弹窗 */}
      {showNewPostModal && (
        <div style={styles.modalOverlay} onClick={() => setShowNewPostModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>发布新帖</h2>
              <button 
                style={styles.closeBtn}
                onClick={() => setShowNewPostModal(false)}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmitPost} style={styles.postForm}>
              <div style={styles.formGroup}>
                <label style={styles.label}>分类</label>
                <select
                  value={newPost.category}
                  onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                  style={styles.select}
                >
                  {categories.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>标题</label>
                <input
                  type="text"
                  placeholder="请输入帖子标题"
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  style={styles.input}
                  maxLength={100}
                />
                <span style={styles.charCount}>{newPost.title.length}/100</span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>内容</label>
                <textarea
                  placeholder="分享你的想法..."
                  value={newPost.content}
                  onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                  style={styles.textarea}
                  rows={8}
                  maxLength={5000}
                />
                <span style={styles.charCount}>{newPost.content.length}/5000</span>
              </div>

              {/* 图片上传区域 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>图片（可选，最多 {MAX_IMAGES} 张）</label>
                
                {/* 已上传图片预览 */}
                {images.length > 0 && (
                  <div style={styles.imagePreviewGrid}>
                    {images.map((img) => (
                      <div key={img.id} style={styles.imagePreviewItem}>
                        <img src={img.url} alt={img.name} style={styles.previewImage} />
                        <button 
                          type="button"
                          style={styles.removeImageBtn}
                          onClick={() => removeImage(img.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 上传区域 */}
                {images.length < MAX_IMAGES && (
                  <div
                    style={{
                      ...styles.uploadArea,
                      borderColor: dragActive ? '#12B7F5' : '#3d4450',
                      backgroundColor: dragActive ? 'rgba(18, 183, 245, 0.1)' : 'transparent',
                    }}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleImageSelect}
                    />
                    {uploadingImages ? (
                      <div style={styles.uploadingIndicator}>
                        <div style={styles.miniSpinner}></div>
                        <span>处理中...</span>
                      </div>
                    ) : (
                      <>
                        <span style={styles.uploadIcon}>📷</span>
                        <span style={styles.uploadText}>
                          点击或拖拽图片到这里上传
                        </span>
                        <span style={styles.uploadHint}>
                          支持 JPG、PNG、GIF，单张最大 5MB
                        </span>
                      </>
                    )}
                  </div>
                )}
                
                {images.length > 0 && (
                  <span style={styles.imageCount}>
                    已选择 {images.length}/{MAX_IMAGES} 张图片
                  </span>
                )}
              </div>

              <div style={styles.formActions}>
                <button 
                  type="button" 
                  style={styles.cancelBtn}
                  onClick={() => setShowNewPostModal(false)}
                >
                  取消
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.submitBtn,
                    opacity: submitting ? 0.7 : 1,
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                  disabled={!newPost.title.trim() || !newPost.content.trim() || submitting}
                >
                  {submitting ? '发布中...' : '发布'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 编辑帖子弹窗 */}
      {showEditModal && editingPost && (
        <div style={styles.modalOverlay} onClick={() => { setShowEditModal(false); setEditingPost(null); }}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>编辑帖子</h2>
              <button 
                style={styles.closeBtn}
                onClick={() => { setShowEditModal(false); setEditingPost(null); }}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleSubmitEdit} style={styles.postForm}>
              <div style={styles.formGroup}>
                <label style={styles.label}>分类</label>
                <select
                  value={editingPost.category}
                  onChange={(e) => setEditingPost({...editingPost, category: e.target.value})}
                  style={styles.select}
                >
                  {categories.filter(c => c.id !== 'all').map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>标题</label>
                <input
                  type="text"
                  placeholder="请输入帖子标题"
                  value={editingPost.title}
                  onChange={(e) => setEditingPost({...editingPost, title: e.target.value})}
                  style={styles.input}
                  maxLength={100}
                />
                <span style={styles.charCount}>{editingPost.title.length}/100</span>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>内容</label>
                <textarea
                  placeholder="分享你的想法..."
                  value={editingPost.content}
                  onChange={(e) => setEditingPost({...editingPost, content: e.target.value})}
                  style={styles.textarea}
                  rows={8}
                  maxLength={5000}
                />
                <span style={styles.charCount}>{editingPost.content.length}/5000</span>
              </div>

              {/* 编辑时的图片管理 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>图片（最多 {MAX_IMAGES} 张）</label>
                
                {editImages.length > 0 && (
                  <div style={styles.imagePreviewGrid}>
                    {editImages.map((img, index) => (
                      <div key={index} style={styles.imagePreviewItem}>
                        <img 
                          src={typeof img === 'string' ? img : img.url} 
                          alt={`图片 ${index + 1}`} 
                          style={styles.previewImage} 
                        />
                        <button 
                          type="button"
                          style={styles.removeImageBtn}
                          onClick={() => removeEditImage(index)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {editImages.length < MAX_IMAGES && (
                  <label style={styles.addImageBtn}>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleEditImageSelect}
                    />
                    📷 添加图片
                  </label>
                )}
                
                {editImages.length > 0 && (
                  <span style={styles.imageCount}>
                    已有 {editImages.length}/{MAX_IMAGES} 张图片
                  </span>
                )}
              </div>

              <div style={styles.formActions}>
                <button 
                  type="button" 
                  style={styles.cancelBtn}
                  onClick={() => { setShowEditModal(false); setEditingPost(null); }}
                >
                  取消
                </button>
                <button 
                  type="submit" 
                  style={{
                    ...styles.submitBtn,
                    opacity: submitting ? 0.7 : 1,
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                  disabled={!editingPost.title.trim() || !editingPost.content.trim() || submitting}
                >
                  {submitting ? '保存中...' : '保存修改'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {deleteConfirm && (
        <div style={styles.modalOverlay} onClick={() => setDeleteConfirm(null)}>
          <div style={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
            <div style={styles.confirmIcon}>🗑️</div>
            <h3 style={styles.confirmTitle}>确认删除</h3>
            <p style={styles.confirmText}>确定要删除这条帖子吗？此操作不可恢复。</p>
            <div style={styles.confirmActions}>
              <button 
                style={styles.cancelBtn}
                onClick={() => setDeleteConfirm(null)}
              >
                取消
              </button>
              <button 
                style={styles.deleteConfirmBtn}
                onClick={() => handleDeletePost(deleteConfirm)}
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 图片查看器组件
function ImageViewer({ images, currentIndex, onClose, onPrev, onNext }) {
  // 键盘导航 - Hook 必须在条件判断之前调用
  React.useEffect(() => {
    if (!images || images.length === 0) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && currentIndex > 0) onPrev();
      if (e.key === 'ArrowRight' && currentIndex < images.length - 1) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images, onClose, onPrev, onNext]);

  if (!images || images.length === 0) return null;
  
  const currentImage = images[currentIndex];
  const imgUrl = typeof currentImage === 'string' ? currentImage : currentImage.url;

  return (
    <div style={styles.imageViewerOverlay} onClick={onClose}>
      <button style={styles.viewerCloseBtn} onClick={onClose}>✕</button>
      
      {images.length > 1 && currentIndex > 0 && (
        <button 
          style={{...styles.viewerNavBtn, left: '20px'}} 
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
        >
          ‹
        </button>
      )}
      
      <div style={styles.viewerImageContainer} onClick={(e) => e.stopPropagation()}>
        <img src={imgUrl} alt="查看大图" style={styles.viewerImage} />
      </div>
      
      {images.length > 1 && currentIndex < images.length - 1 && (
        <button 
          style={{...styles.viewerNavBtn, right: '20px'}} 
          onClick={(e) => { e.stopPropagation(); onNext(); }}
        >
          ›
        </button>
      )}
      
      {images.length > 1 && (
        <div style={styles.viewerCounter}>
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
}

// 帖子卡片组件
function PostCard({ post, formatTime, getCategoryColor, onLike, onEdit, onDelete, isAuthenticated, currentUserId }) {
  const [isHovered, setIsHovered] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showMenu, setShowMenu] = useState(false);

  const openImageViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
  };

  // 判断是否是作者本人
  const isAuthor = currentUserId && post.author_id === currentUserId;

  return (
    <div 
      style={{
        ...styles.postCard,
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered 
          ? '0 12px 40px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.2)',
        borderColor: isHovered ? 'rgba(18, 183, 245, 0.3)' : 'rgba(255, 255, 255, 0.08)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setShowMenu(false); }}
    >
      {/* 置顶标识 */}
      {post.is_pinned === 1 && (
        <div style={styles.pinnedBadge}>
          <span>📌</span>
          <span>置顶</span>
        </div>
      )}

      {/* 帖子头部 */}
      <div style={styles.postHeader}>
        <img 
          src={post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author_nickname || post.author_id}`}
          alt="头像"
          style={styles.authorAvatar}
        />
        <div style={styles.authorInfo}>
          <span style={styles.authorName}>{post.author_nickname || '匿名用户'}</span>
          <span style={styles.postTime}>{formatTime(post.created_at)}</span>
        </div>
        
        {/* 右侧：分类标签 + 操作菜单 */}
        <div style={styles.postHeaderRight}>
          <span 
            style={{
              ...styles.categoryBadge,
              backgroundColor: `${getCategoryColor(post.category)}20`,
              color: getCategoryColor(post.category),
              borderColor: getCategoryColor(post.category),
            }}
          >
            {post.category}
          </span>
          
          {/* 操作菜单按钮 - 只对作者显示 */}
          {isAuthor && (
            <div style={styles.postMenuContainer}>
              <button 
                style={styles.postMenuBtn}
                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
              >
                ⋮
              </button>
              {showMenu && (
                <div style={styles.postMenu}>
                  <button 
                    style={styles.postMenuItem}
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onEdit(post); }}
                  >
                    ✏️ 编辑
                  </button>
                  <button 
                    style={{...styles.postMenuItem, color: '#ef4444'}}
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDelete(post.id); }}
                  >
                    🗑️ 删除
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 帖子内容 */}
      <h3 style={styles.postTitle}>{post.title}</h3>
      <p style={styles.postContent}>{post.content}</p>

      {/* 帖子图片 */}
      {post.images && post.images.length > 0 && (
        <div style={styles.postImages}>
          {post.images.slice(0, 4).map((img, index) => (
            <div 
              key={index} 
              style={{
                ...styles.postImageWrapper,
                ...(post.images.length === 1 ? styles.singleImage : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                openImageViewer(index);
              }}
            >
              <img 
                src={typeof img === 'string' ? img : img.url} 
                alt={`图片 ${index + 1}`}
                style={styles.postImage}
              />
              {index === 3 && post.images.length > 4 && (
                <div style={styles.moreImagesOverlay}>
                  +{post.images.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 图片查看器 */}
      {viewerOpen && post.images && (
        <ImageViewer
          images={post.images}
          currentIndex={viewerIndex}
          onClose={() => setViewerOpen(false)}
          onPrev={() => setViewerIndex(prev => Math.max(0, prev - 1))}
          onNext={() => setViewerIndex(prev => Math.min(post.images.length - 1, prev + 1))}
        />
      )}

      {/* 帖子统计 */}
      <div style={styles.postStats}>
        <div style={styles.statItem}>
          <span style={styles.statIcon}>👁️</span>
          <span>{post.views || 0}</span>
        </div>
        <div 
          style={{
            ...styles.statItem,
            cursor: isAuthenticated ? 'pointer' : 'default',
          }}
          onClick={() => isAuthenticated && onLike(post.id)}
        >
          <span style={styles.statIcon}>❤️</span>
          <span>{post.likes || 0}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statIcon}>💬</span>
          <span>{post.comment_count || 0}</span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '90vh',
    background: 'linear-gradient(180deg, #1a1d23 0%, #282c34 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgDecoration: {
    position: 'absolute',
    top: '-50%',
    right: '-20%',
    width: '80%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(18, 183, 245, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    zIndex: 10,
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '32px',
    flexWrap: 'wrap',
    gap: '20px',
  },
  titleSection: {},
  pageTitle: {
    fontSize: '36px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  titleIcon: {
    fontSize: '32px',
  },
  pageSubtitle: {
    fontSize: '16px',
    color: '#9ca3af',
  },
  newPostBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(18, 183, 245, 0.3)',
  },
  plusIcon: {
    fontSize: '20px',
    fontWeight: '300',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(37, 40, 48, 0.9)',
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '24px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  searchIcon: {
    fontSize: '18px',
    marginRight: '12px',
  },
  searchInput: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#fff',
    fontSize: '15px',
  },
  categoryBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '32px',
  },
  categoryTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1px solid',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'transparent',
  },
  postList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  postCard: {
    backgroundColor: 'rgba(37, 40, 48, 0.9)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    WebkitBackdropFilter: 'blur(10px)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
  },
  pinnedBadge: {
    position: 'absolute',
    top: '12px',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 10px',
    backgroundColor: 'rgba(245, 158, 11, 0.2)',
    borderRadius: '12px',
    fontSize: '12px',
    color: '#F59E0B',
  },
  postHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  authorAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    objectFit: 'cover',
  },
  authorInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  authorName: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
  },
  postTime: {
    color: '#6b7280',
    fontSize: '12px',
  },
  categoryBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    border: '1px solid',
  },
  postTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    lineHeight: '1.4',
  },
  postContent: {
    color: '#9ca3af',
    fontSize: '14px',
    lineHeight: '1.7',
    marginBottom: '16px',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
  },
  postStats: {
    display: 'flex',
    gap: '24px',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#6b7280',
    fontSize: '13px',
  },
  statIcon: {
    fontSize: '14px',
  },
  // Loading state
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(18, 183, 245, 0.2)',
    borderTop: '3px solid #12B7F5',
    borderRadius: '50%',
    margin: '0 auto 16px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#9ca3af',
    fontSize: '14px',
  },
  // Error state
  errorState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  errorIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '14px',
    marginBottom: '16px',
  },
  retryBtn: {
    padding: '10px 24px',
    backgroundColor: '#12B7F5',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '16px',
    marginBottom: '20px',
  },
  createFirstBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  loginPrompt: {
    textAlign: 'center',
    padding: '24px',
    backgroundColor: 'rgba(18, 183, 245, 0.1)',
    borderRadius: '12px',
    marginTop: '24px',
    color: '#12B7F5',
    fontSize: '14px',
  },
  // 弹窗样式
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
    WebkitBackdropFilter: 'blur(4px)',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    backgroundColor: '#252830',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  modalTitle: {
    color: '#fff',
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px',
    transition: 'color 0.2s',
  },
  postForm: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
    position: 'relative',
  },
  label: {
    display: 'block',
    color: '#d1d5db',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
  },
  select: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#1a1d23',
    border: '1px solid #3d4450',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#1a1d23',
    border: '1px solid #3d4450',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: '#1a1d23',
    border: '1px solid #3d4450',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  charCount: {
    position: 'absolute',
    right: '12px',
    bottom: '-20px',
    fontSize: '12px',
    color: '#6b7280',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '32px',
  },
  cancelBtn: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: '1px solid #3d4450',
    borderRadius: '10px',
    color: '#9ca3af',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitBtn: {
    padding: '12px 32px',
    background: 'linear-gradient(135deg, #12B7F5 0%, #0099E5 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  // 图片上传样式
  uploadArea: {
    border: '2px dashed #3d4450',
    borderRadius: '12px',
    padding: '32px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  uploadIcon: {
    fontSize: '36px',
  },
  uploadText: {
    color: '#9ca3af',
    fontSize: '14px',
  },
  uploadHint: {
    color: '#6b7280',
    fontSize: '12px',
  },
  uploadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#12B7F5',
    fontSize: '14px',
  },
  miniSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(18, 183, 245, 0.2)',
    borderTop: '2px solid #12B7F5',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  imagePreviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    marginBottom: '16px',
  },
  imagePreviewItem: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: '#1a1d23',
  },
  previewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  removeImageBtn: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    border: 'none',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
  },
  imageCount: {
    display: 'block',
    marginTop: '8px',
    fontSize: '12px',
    color: '#6b7280',
    textAlign: 'right',
  },
  // 帖子图片显示样式
  postImages: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px',
    marginBottom: '16px',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  postImageWrapper: {
    position: 'relative',
    aspectRatio: '1',
    backgroundColor: '#1a1d23',
    overflow: 'hidden',
    cursor: 'zoom-in',
  },
  singleImage: {
    gridColumn: 'span 2',
    aspectRatio: '16/9',
  },
  postImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  moreImagesOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '24px',
    fontWeight: '600',
  },
  // 图片查看器样式
  imageViewerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerCloseBtn: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#fff',
    fontSize: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    zIndex: 10,
  },
  viewerNavBtn: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#fff',
    fontSize: '32px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    zIndex: 10,
  },
  viewerImageContainer: {
    maxWidth: '90vw',
    maxHeight: '85vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewerImage: {
    maxWidth: '100%',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  viewerCounter: {
    position: 'absolute',
    bottom: '30px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    padding: '8px 20px',
    borderRadius: '20px',
    color: '#fff',
    fontSize: '14px',
  },
  // 帖子头部右侧区域
  postHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  // 帖子操作菜单样式
  postMenuContainer: {
    position: 'relative',
  },
  postMenuBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#9ca3af',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  postMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: '#252830',
    borderRadius: '12px',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    overflow: 'hidden',
    minWidth: '120px',
  },
  postMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#d1d5db',
    fontSize: '14px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.15s',
  },
  // 添加图片按钮
  addImageBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px dashed #3d4450',
    borderRadius: '10px',
    color: '#9ca3af',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  // 删除确认对话框
  confirmDialog: {
    backgroundColor: '#252830',
    borderRadius: '20px',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
  },
  confirmIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  confirmTitle: {
    color: '#fff',
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  confirmText: {
    color: '#9ca3af',
    fontSize: '14px',
    marginBottom: '24px',
    lineHeight: '1.6',
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  deleteConfirmBtn: {
    padding: '12px 24px',
    backgroundColor: '#ef4444',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default Forum;
